import React, { useState } from 'react';
import { format, isSameDay } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Calendar, User, Phone, Mail, MapPin, Home, MoreVertical, DollarSign, Briefcase, List, Grid } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useToast } from '../hooks/use-toast';
import { Agent, AgentInsert } from '../../../shared/schema';
import { apiRequest } from '../lib/queryClient';

// Agent list item: from agents table; may be linked to team user (contractorUserId + teamRole)
export type AgentListItem = Agent & { contractorUserId?: number | null; teamRole?: string | null };

function formatAgentType(agent: AgentListItem): string {
  const r = (agent.role || '').trim();
  if (r === 'field_agent') return 'Field agent';
  if (r === 'senior_agent') return 'Senior agent';
  if (r === 'lead_estimator') return 'Lead estimator';
  return r ? r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'N/A';
}

function formatTeamRole(teamRole: string | null | undefined): string {
  if (!teamRole) return '';
  return teamRole.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function safeFormatDate(val: unknown): string {
  if (val == null) return '-';
  try {
    const d = typeof val === 'number' ? new Date(val < 1e12 ? val * 1000 : val) : new Date(val as string);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '-';
  }
}

function isLinkedToTeam(agent: AgentListItem): boolean {
  return !!(agent.contractorUserId ?? agent.teamRole);
}
import AgentScheduler from '../components/agents/AgentScheduler';
import { Link } from 'wouter';
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';
import { authInterceptor } from '../lib/auth-interceptor';
import { formatPhone, formatPhoneInput } from '../lib/validation';

// Work Types for Workers
const WORK_TYPES = [
  'Helper',
  'General Labor',
  'Cleaning',
  'Site Prep',
  'Demolition',
  'Excavation',
  'Grading',
  'Concrete',
  'Masonry',
  'Framing',
  'Carpentry',
  'Roofing',
  'Siding',
  'Windows',
  'Doors',
  'Gutters',
  'Insulation',
  'Drywall',
  'Flooring',
  'Tile Work',
  'Painting',
  'Cabinetry',
  'Countertops',
  'Electrical',
  'Plumbing',
  'HVAC',
  'Welding',
  'Sheet Metal',
  'Fencing',
  'Decking',
  'Landscaping',
  'Paving',
  'Material Handling',
  'Equipment Operator',
  'Safety Inspector',
  'Supervisor/Foreman',
  'Quality Control'
];

export default function AgentManagementPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [editingAgent, setEditingAgent] = useState<AgentListItem | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [showWorkerDialog, setShowWorkerDialog] = useState(false);
  const [editingWorker, setEditingWorker] = useState<any>(null); // Default to list view for workers

  // Form state
  const [formData, setFormData] = useState<any>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    employeeId: '',
    role: 'field_agent',
    isActive: true,
    specialties: [],
    hourlyRate: '',
    commissionRate: '',
    hireDate: '',
    notes: '',
    colorCode: '#3B82F6'
  });

  // Worker form state
  const [workerFormData, setWorkerFormData] = useState<any>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    employeeId: '',
    workTypes: [],
    hourlyRate: '',
    isActive: true,
    hireDate: '',
    notes: '',
  });

  // Assignment form state
  const [assignmentData, setAssignmentData] = useState<any>({
    selectedDate: new Date().toISOString().split('T')[0],
    appointmentId: '',
    newAgentId: ''
  });

  // Predefined color options for agents
  const colorOptions = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Green', value: '#10B981' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Orange', value: '#F59E0B' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Cyan', value: '#06B6D4' },
    { name: 'Lime', value: '#84CC16' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Indigo', value: '#6366F1' },
    { name: 'Yellow', value: '#EAB308' },
    { name: 'Teal', value: '#14B8A6' },
    { name: 'Rose', value: '#F43F5E' }
  ];

  // Fetch agents (field agents + team users with role agent)
  const {
    data: agents = [],
    isLoading,
    isError,
    error,
    refetch: refetchAgents,
  } = useQuery<AgentListItem[]>({
    queryKey: ['/api/protected/agents'],
    queryFn: async () => {
      const response = await fetch('/api/protected/agents', {
        headers: {
          ...authInterceptor.addAuthHeaders()
        },
        credentials: 'include',
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const msg = body?.message || body?.error || `Failed to load agents (${response.status})`;
        throw new Error(msg);
      }
      const data = await response.json();
      if (!Array.isArray(data)) return [];
      return data;
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Fetch workers
  const { data: workers = [], isLoading: isLoadingWorkers } = useQuery<any[]>({
    queryKey: ['/api/protected/workers'],
    queryFn: async () => {
      const response = await fetch('/api/protected/workers', {
        headers: {
          ...authInterceptor.addAuthHeaders()
        },
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch workers');
      }
      return response.json();
    },
  });

  // Fetch appointments for assignment
  const { data: appointments = [] } = useQuery({
    onSuccess: (data) => {
      console.log("🔍 Debug - Loaded Appointments:", data);
    },
    queryKey: ['/api/protected/events'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/protected/events");
      return await response.json();
    },
  });

  // Create agent mutation
  const createAgentMutation = useMutation({
    mutationFn: async (agentData: Partial<AgentInsert>) => {
      const response = await fetch('/api/protected/agents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authInterceptor.addAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(agentData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create agent');
      }
      return response.json();
    },
    onSuccess: async (data: AgentListItem) => {
      toast({
        title: "Agent Created",
        description: "The agent has been successfully created.",
      });
      // Optimistic update: show new agent immediately from API response (in case refetch returns stale/cached empty)
      const newItem: AgentListItem = {
        ...data,
        contractorUserId: data.contractorUserId ?? null,
        teamRole: data.teamRole ?? null,
      };
      queryClient.setQueryData<AgentListItem[]>(['/api/protected/agents'], (old) => {
        const list = old ?? [];
        if (list.some((a) => a.id === newItem.id)) return list;
        return [newItem, ...list];
      });
      queryClient.invalidateQueries({ queryKey: ['/api/protected/agents'] });
      await queryClient.refetchQueries({ queryKey: ['/api/protected/agents'] });
      setShowAgentDialog(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Creation Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update agent mutation
  const updateAgentMutation = useMutation({
    mutationFn: async ({ id, ...agentData }: Partial<AgentInsert> & { id: number }) => {
      const response = await fetch(`/api/protected/agents?id=${id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          ...authInterceptor.addAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ ...agentData, id }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update agent');
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "✅ Success!",
        description: "The agent has been successfully updated.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/protected/agents'] });
      setShowAgentDialog(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete agent mutation (POST to agents-delete to avoid 405 routing issues)
  const deleteAgentMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest('POST', '/api/protected/agents-delete', { id });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to delete agent');
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Agent Deleted",
        description: "The agent has been successfully deleted.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/protected/agents'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Deletion Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Assign appointment mutation
  const assignEstimateMutation = useMutation({
    mutationFn: async (assignmentData: any) => {
      // Update the appointment with the new agent
      const eventResponse = await fetch(`/api/protected/events-patch?id=${assignmentData.appointmentId}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          ...authInterceptor.addAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          agentId: parseInt(assignmentData.newAgentId)
        }),
      });
      
      const responseText = await eventResponse.text();
      console.log("🔍 Debug - Response Status:", eventResponse.status);
      console.log("🔍 Debug - Response Headers:", eventResponse.headers);
      console.log("🔍 Debug - Raw Response Text:", responseText);
      
      if (!eventResponse.ok) {
        const errorText = responseText;
        console.log("🔍 Debug - Error Response:", errorText);
        let error;
        try {
          error = JSON.parse(errorText);
        } catch {
          error = { message: errorText || "Unknown error" };
        }
        // Error handling moved above
        throw new Error(error.message || 'Failed to update appointment');
      }
      
      console.log("🔍 Debug - Raw Response Text:", responseText);
      if (!responseText || responseText.trim() === "") {
        console.log("🔍 Debug - Empty response, returning success");
        return { success: true, message: "Agent updated successfully" };
      }
      console.log("🔍 Debug - Response Body:", responseText);
      try {
        return JSON.parse(responseText);
      } catch (parseError) {
        console.log("🔍 Debug - JSON Parse Error:", parseError);
        console.log("🔍 Debug - Response that failed to parse:", responseText);
        return { success: true, message: "Agent updated successfully (non-JSON response)" };
      }
    },
    onSuccess: () => {
      toast({
        title: "✅ Success!",
        description: "🎯 Appointment successfully reassigned! The change has been saved to the database.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/protected/events'] });
      setAssignmentData({
        selectedDate: new Date().toISOString().split('T')[0],
        appointmentId: '',
        newAgentId: ''
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Assignment Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      employeeId: '',
      role: 'field_agent',
      isActive: true,
      specialties: [],
      hourlyRate: '',
      commissionRate: '',
      hireDate: '',
      notes: '',
      colorCode: '#3B82F6'
    });
    setEditingAgent(null);
    setShowAgentDialog(false); // Close dialog when resetting
  };

  const openNewAgentDialog = () => {
    resetForm();
    setShowAgentDialog(true); // Open dialog for new agent
  };

  const handleEditAgent = (agent: AgentListItem) => {
    if (isLinkedToTeam(agent)) {
      window.location.href = '/settings/team';
      return;
    }
    setFormData({
      firstName: agent.firstName,
      lastName: agent.lastName,
      email: agent.email || '',
      phone: agent.phone,
      employeeId: agent.employeeId || '',
      role: agent.role,
      isActive: agent.isActive,
      specialties: agent.specialties as string[],
      hourlyRate: agent.hourlyRate ? agent.hourlyRate.toString() : undefined,
      commissionRate: agent.commissionRate ? agent.commissionRate.toString() : undefined,
      hireDate: agent.hireDate || undefined,
      notes: agent.notes || '',
      colorCode: agent.colorCode || '#3B82F6'
    });
    setEditingAgent(agent);
    setShowAgentDialog(true);
  };

  const handleSubmit = () => {
    console.log('🔍 Agent form submission:', formData);
    
    if (!formData.firstName || !formData.lastName || !formData.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    if (editingAgent) {
      console.log('🔍 Updating agent:', editingAgent.id, formData);
      updateAgentMutation.mutate({ ...formData, id: Number(editingAgent.id) } as any);
    } else {
      console.log('🔍 Creating new agent:', formData);
      createAgentMutation.mutate(formData);
    }
  };

  const handleDeleteAgent = (agent: AgentListItem) => {
    if (isLinkedToTeam(agent)) {
      window.location.href = '/settings/team';
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${agent.firstName} ${agent.lastName}?`)) {
      deleteAgentMutation.mutate(Number(agent.id));
    }
  };

  // Worker handlers
  const openNewWorkerDialog = () => {
    setEditingWorker(null);
    setWorkerFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      employeeId: '',
      workTypes: [],
      hourlyRate: '',
      isActive: true,
      hireDate: '',
      notes: '',
    });
    setShowWorkerDialog(true);
  };

  const handleEditWorker = (worker: any) => {
    setEditingWorker(worker);
    setWorkerFormData({
      firstName: worker.firstName,
      lastName: worker.lastName,
      email: worker.email || '',
      phone: worker.phone || '',
      employeeId: worker.employeeId || '',
      workTypes: worker.workTypes || [],
      hourlyRate: worker.hourlyRate?.toString() || '',
      isActive: worker.isActive,
      hireDate: worker.hireDate || '',
      notes: worker.notes || '',
    });
    setShowWorkerDialog(true);
  };

  const handleDeleteWorker = async (worker: any) => {
    if (!confirm(`Are you sure you want to delete ${worker.firstName} ${worker.lastName}?`)) {
      return;
    }
    try {
      await apiRequest('DELETE', '/api/protected/workers', { id: worker.id });
      toast({
        title: 'Worker Deleted',
        description: `${worker.firstName} ${worker.lastName} has been deleted.`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/protected/workers'] });
      queryClient.invalidateQueries({ queryKey: ['/api/timeclock/workers'] });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete worker',
        variant: 'destructive',
      });
    }
  };

  const saveWorkerMutation = useMutation({
    mutationFn: async (data: any) => {
      const payload = {
        ...data,
        hourlyRate: data.hourlyRate ? parseFloat(data.hourlyRate) : null,
        workTypes: Array.isArray(data.workTypes) ? data.workTypes : [],
      };
      if (editingWorker) {
        return await apiRequest('PUT', '/api/protected/workers', { id: editingWorker.id, ...payload });
      } else {
        return await apiRequest('POST', '/api/protected/workers', payload);
      }
    },
    onSuccess: () => {
      toast({
        title: editingWorker ? 'Worker Updated' : 'Worker Created',
        description: editingWorker ? 'Worker has been updated successfully.' : 'Worker has been created successfully.',
      });
      setShowWorkerDialog(false);
      queryClient.invalidateQueries({ queryKey: ['/api/protected/workers'] });
      queryClient.invalidateQueries({ queryKey: ['/api/timeclock/workers'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save worker',
        variant: 'destructive',
      });
    },
  });

  const toggleWorkType = (workType: string) => {
    const current = workerFormData.workTypes || [];
    if (current.includes(workType)) {
      setWorkerFormData({ ...workerFormData, workTypes: current.filter((t: string) => t !== workType) });
    } else {
      setWorkerFormData({ ...workerFormData, workTypes: [...current, workType] });
    }
  };



  const handleAssignSubmit = () => {
    if (!assignmentData.appointmentId || !assignmentData.newAgentId || assignmentData.newAgentId === "") {
      toast({
        title: "Missing Information",
        description: "Please select an appointment and a new agent.",
        variant: "destructive",
      });
      return;
    }

    const assignmentPayload = {
      appointmentId: assignmentData.appointmentId,
      newAgentId: assignmentData.newAgentId
    };

    console.log("🔍 Debug - About to call mutation with:", assignmentPayload);
    assignEstimateMutation.mutate(assignmentPayload);
  };

  const specialtyOptions = [
    'fencing', 'roofing', 'siding', 'gutters', 'windows', 'doors', 
    'flooring', 'painting', 'electrical', 'plumbing', 'hvac'
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-6 md:p-8 pt-24 flex flex-col items-center">
            <div className="w-full space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-[var(--dash-text)]">Agent Management</h1>
                  <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Agents are the ones who create and do estimates. Manage your field agents, schedules, and assignments.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button className="h-10 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white" onClick={() => openNewAgentDialog()}>
                    <Plus className="h-4 w-4 mr-2" />Add New Agent
                  </Button>
                  <Link href="/calendar">
                    <Button variant="outline" size="sm" className="h-10 rounded-lg border-[var(--dash-border)]">
                      <Calendar className="h-4 w-4 mr-2" />Calendar
                    </Button>
                  </Link>
                  <Link href="/dashboard">
                    <Button variant="outline" size="sm" className="h-10 rounded-lg border-[var(--dash-border)]">
                      <Home className="h-4 w-4 mr-2" />Dashboard
                    </Button>
                  </Link>
                </div>
              </div>
      <Dialog open={showAgentDialog} onOpenChange={setShowAgentDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto dashboard-2025 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-slate-100 shadow-xl rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-[var(--dash-text)] text-xl font-bold">
              {editingAgent ? 'Edit Agent' : 'Add New Agent'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className="text-[var(--dash-text)] font-medium mb-2">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  placeholder="John"
                  className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]"
                />
              </div>
              <div>
                <Label htmlFor="lastName" className="text-[var(--dash-text)] font-medium mb-2">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  placeholder="Doe"
                  className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="employeeId">Employee ID</Label>
                <Input
                  id="employeeId"
                  value={formData.employeeId}
                  onChange={(e) => setFormData({...formData, employeeId: e.target.value})}
                  placeholder="EMP001"
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({...formData, role: value as any})}
                >
                  <SelectTrigger className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="field_agent">Field Agent</SelectItem>
                    <SelectItem value="senior_agent">Senior Agent</SelectItem>
                    <SelectItem value="lead_estimator">Lead Estimator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hourlyRate">Hourly Rate ($)</Label>
                <Input
                  id="hourlyRate"
                  type="number"
                  step="0.01"
                  value={formData.hourlyRate || ''}
                  onChange={(e) => setFormData({...formData, hourlyRate: e.target.value})}
                  placeholder="25.00"
                />
              </div>
              <div>
                <Label htmlFor="commissionRate">Commission Rate (%)</Label>
                <Input
                  id="commissionRate"
                  type="number"
                  step="0.01"
                  value={formData.commissionRate || ''}
                  onChange={(e) => setFormData({...formData, commissionRate: e.target.value})}
                  placeholder="5.00"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="hireDate">Hire Date</Label>
              <Input
                id="hireDate"
                type="date"
                value={formData.hireDate || ''}
                onChange={(e) => setFormData({...formData, hireDate: e.target.value || undefined})}
              />
            </div>

            <div>
              <Label htmlFor="colorCode">Calendar Color</Label>
              <div className="grid grid-cols-6 gap-2 mt-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`w-10 h-10 rounded-lg border-2 transition-all hover:scale-105 ${
                      formData.colorCode === color.value 
                        ? 'border-[var(--dash-accent)] ring-2 ring-[var(--dash-border)]' 
                        : 'border-[var(--dash-border)] hover:border-[var(--dash-text-muted)]'
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setFormData({...formData, colorCode: color.value})}
                    title={color.name}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <div 
                  className="w-4 h-4 rounded-full border border-[var(--dash-border)]" 
                  style={{ backgroundColor: formData.colorCode }}
                />
                <span className="text-sm text-[var(--dash-text-muted)]">
                  Selected: {colorOptions.find(c => c.value === formData.colorCode)?.name || 'Custom'}
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({...formData, isActive: checked})}
              />
              <Label htmlFor="isActive">Active Agent</Label>
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes about the agent..."
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowAgentDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={createAgentMutation.isPending || updateAgentMutation.isPending}
              >
                {editingAgent ? 'Update Agent' : 'Create Agent'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Worker Dialog */}
      <Dialog open={showWorkerDialog} onOpenChange={setShowWorkerDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto dashboard-2025 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-slate-100 shadow-xl rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-[var(--dash-text)] text-xl font-bold">
              {editingWorker ? 'Edit Worker' : 'Add New Worker'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="workerFirstName" className="text-[var(--dash-text)] font-medium mb-2">First Name *</Label>
                <Input
                  id="workerFirstName"
                  value={workerFormData.firstName}
                  onChange={(e) => setWorkerFormData({...workerFormData, firstName: e.target.value})}
                  placeholder="John"
                  className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]"
                />
              </div>
              <div>
                <Label htmlFor="workerLastName" className="text-[var(--dash-text)] font-medium mb-2">Last Name *</Label>
                <Input
                  id="workerLastName"
                  value={workerFormData.lastName}
                  onChange={(e) => setWorkerFormData({...workerFormData, lastName: e.target.value})}
                  placeholder="Doe"
                  className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="workerEmail">Email</Label>
                <Input
                  id="workerEmail"
                  type="email"
                  value={workerFormData.email}
                  onChange={(e) => setWorkerFormData({...workerFormData, email: e.target.value})}
                  placeholder="john@example.com"
                  className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]"
                />
              </div>
              <div>
                <Label htmlFor="workerPhone">Phone</Label>
                <Input
                  id="workerPhone"
                  type="tel"
                  value={workerFormData.phone}
                  onChange={(e) => {
                    const formatted = formatPhoneInput(e.target.value);
                    setWorkerFormData({...workerFormData, phone: formatted});
                  }}
                  placeholder="(555) 123-4567"
                  className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]"
                  maxLength={14}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="workerEmployeeId">Employee ID</Label>
                <Input
                  id="workerEmployeeId"
                  value={workerFormData.employeeId}
                  onChange={(e) => setWorkerFormData({...workerFormData, employeeId: e.target.value})}
                  placeholder="EMP001"
                  className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]"
                />
              </div>
              <div>
                <Label htmlFor="workerHourlyRate">Hourly Rate ($)</Label>
                <Input
                  id="workerHourlyRate"
                  type="number"
                  step="0.01"
                  value={workerFormData.hourlyRate}
                  onChange={(e) => setWorkerFormData({...workerFormData, hourlyRate: e.target.value})}
                  placeholder="25.00"
                  className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]"
                />
              </div>
            </div>

            <div>
              <Label className="text-[var(--dash-text)] font-medium mb-2">Work Types (Select all that apply)</Label>
              <div className="grid grid-cols-3 gap-2 mt-2 p-4 border border-[var(--dash-border)] rounded-lg bg-[var(--dash-bg)] max-h-64 overflow-y-auto">
                {WORK_TYPES.map((workType) => (
                  <label key={workType} className="flex items-center gap-2 cursor-pointer text-[var(--dash-text)] hover:text-[var(--dash-accent)]">
                    <input
                      type="checkbox"
                      checked={workerFormData.workTypes?.includes(workType) || false}
                      onChange={() => toggleWorkType(workType)}
                      className="rounded"
                    />
                    <span className="text-sm">{workType}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="workerHireDate">Hire Date</Label>
                <Input
                  id="workerHireDate"
                  type="date"
                  value={workerFormData.hireDate}
                  onChange={(e) => setWorkerFormData({...workerFormData, hireDate: e.target.value})}
                  className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]"
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Switch
                  id="workerIsActive"
                  checked={workerFormData.isActive}
                  onCheckedChange={(checked) => setWorkerFormData({...workerFormData, isActive: checked})}
                />
                <Label htmlFor="workerIsActive" className="text-[var(--dash-text)]">Active Worker</Label>
              </div>
            </div>

            <div>
              <Label htmlFor="workerNotes">Notes</Label>
              <Textarea
                id="workerNotes"
                value={workerFormData.notes}
                onChange={(e) => setWorkerFormData({...workerFormData, notes: e.target.value})}
                placeholder="Additional notes about the worker..."
                rows={3}
                className="rounded-lg border-[var(--dash-border)] bg-[var(--dash-bg)]"
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowWorkerDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => saveWorkerMutation.mutate(workerFormData)}
                disabled={saveWorkerMutation.isPending || !workerFormData.firstName || !workerFormData.lastName}
                className="h-10 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white"
              >
                {saveWorkerMutation.isPending ? 'Saving...' : editingWorker ? 'Update Worker' : 'Create Worker'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="agents" className="space-y-6">
        <TabsList className="rounded-lg border border-[var(--dash-border)] bg-[var(--dash-card)] p-1">
          <TabsTrigger value="agents" className="dash-tab-active dash-tab-inactive flex items-center px-4 py-2.5">
            <User className="h-5 w-5 mr-2" />
            <span className="font-medium">Agents</span>
          </TabsTrigger>
          <TabsTrigger value="schedule" className="dash-tab-active dash-tab-inactive flex items-center px-4 py-2.5">
            <MapPin className="h-5 w-5 mr-2" />
            <span className="font-medium">Daily Schedule</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="agents" className="space-y-6">
          {/* Combined Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="dash-stat-card p-4 text-center">
              <div className="dash-stat-number">{agents.length}</div>
              <div className="text-sm text-[var(--dash-text-muted)]">Total Agents</div>
            </div>
            <div className="dash-stat-card p-4 text-center">
              <div className="dash-stat-number">{workers.length}</div>
              <div className="text-sm text-[var(--dash-text-muted)]">Total Workers</div>
            </div>
            <div className="dash-stat-card p-4 text-center">
              <div className="dash-stat-number">
                {agents.filter(a => a.isActive).length}
              </div>
              <div className="text-sm text-[var(--dash-text-muted)]">Active Agents</div>
            </div>
            <div className="dash-stat-card p-4 text-center">
              <div className="dash-stat-number">
                {workers.filter(w => w.isActive).length}
              </div>
              <div className="text-sm text-[var(--dash-text-muted)]">Active Workers</div>
            </div>
          </div>

          {/* AGENTS SECTION */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[var(--dash-text)]">Agents</h2>
              <Button onClick={openNewAgentDialog} className="h-10 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white">
                <Plus className="h-4 w-4 mr-2" />
                Add Agent
              </Button>
            </div>

            {/* View Mode Toggle for Agents */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-[var(--dash-text-muted)]">View Mode</span>
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={`h-8 ${viewMode === 'list' ? 'bg-[var(--dash-accent)] text-white hover:opacity-90' : 'border-[var(--dash-border)] text-[var(--dash-text)] hover:bg-[var(--dash-card)]'}`}
                >
                  <List className="h-4 w-4 mr-1" />
                  List
                </Button>
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className={`h-8 ${viewMode === 'grid' ? 'bg-[var(--dash-accent)] text-white hover:opacity-90' : 'border-[var(--dash-border)] text-[var(--dash-text)] hover:bg-[var(--dash-card)]'}`}
                >
                  <Grid className="h-4 w-4 mr-1" />
                  Grid
                </Button>
              </div>
            </div>

            {/* Agents List View */}
            {viewMode === 'list' ? (
            <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-[var(--dash-border)] bg-[var(--dash-card)]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">Worker</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">Job Types</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">Hourly Rate</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--dash-border)]">
                    {agents.map((agent) => {
                      // Safely parse specialties - handle all possible formats
                      let specialties: string[] = [];
                      if (agent.specialties) {
                        if (Array.isArray(agent.specialties)) {
                          specialties = agent.specialties.filter(s => typeof s === 'string');
                        } else if (typeof agent.specialties === 'string') {
                          try {
                            const parsed = JSON.parse(agent.specialties);
                            specialties = Array.isArray(parsed) ? parsed.filter(s => typeof s === 'string') : [];
                          } catch {
                            specialties = [];
                          }
                        }
                      }
                      
                      return (
                        <tr 
                          key={String(agent.id)} 
                          className="hover:bg-[var(--dash-card)] transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="relative flex-shrink-0">
                                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[var(--dash-card)] border-2 border-[var(--dash-border)]">
                                  <User className="h-5 w-5 text-[var(--dash-accent)]" />
                                </div>
                                <div 
                                  className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[var(--dash-card)]"
                                  style={{ backgroundColor: agent.colorCode || '#3B82F6' }}
                                />
                              </div>
                              <div>
                                <div className="font-semibold text-[var(--dash-text)]">
                                  {agent.firstName} {agent.lastName}
                                </div>
                                {agent.employeeId && (
                                  <div className="text-xs text-[var(--dash-text-muted)]">
                                    ID: {agent.employeeId}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Badge variant="outline" className="text-xs bg-[var(--dash-card)] text-[var(--dash-text)] border-[var(--dash-border)]">
                                {formatAgentType(agent)}
                              </Badge>
                              {agent.teamRole && (
                                <Badge variant="secondary" className="text-xs">
                                  Team: {formatTeamRole(agent.teamRole)}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1.5">
                              {specialties.length > 0 ? (
                                specialties.map((specialty: string, idx: number) => (
                                  <Badge 
                                    key={idx}
                                    variant="outline" 
                                    className="text-xs bg-[var(--dash-card)] text-[var(--dash-text)] border-[var(--dash-border)]"
                                  >
                                    <Briefcase className="h-3 w-3 mr-1 inline" />
                                    {specialty}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-xs text-[var(--dash-text-muted)] italic">No specialties</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {agent.hourlyRate ? (
                              <div className="flex items-center gap-1.5 text-[var(--dash-accent)] font-semibold">
                                <DollarSign className="h-4 w-4" />
                                <span>${Number(agent.hourlyRate).toFixed(2)}/hr</span>
                              </div>
                            ) : (
                              <span className="text-xs text-[var(--dash-text-muted)]">Not set</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge 
                              variant={agent.isActive ? "default" : "secondary"}
                              className={`text-xs ${agent.isActive ? "dash-pill-active" : "dash-pill-inactive"}`}
                            >
                              {agent.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-[var(--dash-text-muted)] hover:text-[var(--dash-text)] hover:bg-[var(--dash-card)]"
                                  title="Actions"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-[var(--dash-card)] border border-[var(--dash-border)]" align="end">
                                {isLinkedToTeam(agent) ? (
                                  <DropdownMenuItem asChild>
                                    <Link href="/settings/team" className="flex items-center cursor-pointer text-[var(--dash-text)] hover:bg-[var(--dash-bg)]">
                                      <User className="h-4 w-4 mr-2" />
                                      <span>Manage in Team</span>
                                    </Link>
                                  </DropdownMenuItem>
                                ) : (
                                  <>
                                    <DropdownMenuItem 
                                      onClick={() => handleEditAgent(agent)}
                                      className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                    >
                                      <Edit2 className="h-4 w-4 mr-2" />
                                      <span>Edit</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => handleDeleteAgent(agent)}
                                      className="text-[var(--dash-pill-inactive)] hover:bg-[var(--dash-bg)] cursor-pointer"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      <span>Delete</span>
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {agents.length === 0 && (
                  <div className="text-center py-12">
                    <User className="h-12 w-12 mx-auto mb-3 text-[var(--dash-text-muted)]" />
                    <p className="text-[var(--dash-text-muted)]">No agents found. Add your first agent to get started.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Grid View - Original Card Layout */
            <div className="grid gap-4">
            {agents.map((agent) => (
              <div key={String(agent.id)} className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] p-4 hover:shadow-[var(--dash-shadow)] transition-all duration-200">
                <div className="flex items-start justify-between gap-4">
                  {/* Left: Agent Info */}
                  <div className="flex items-start gap-3 flex-1">
                    <div className="relative flex-shrink-0">
                      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[var(--dash-card)] border-2 border-[var(--dash-border)]">
                        <User className="h-6 w-6 text-[var(--dash-accent)]" />
                      </div>
                      <div 
                        className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-[var(--dash-card)] shadow-sm"
                        style={{ backgroundColor: agent.colorCode || '#3B82F6' }}
                        title="Calendar Color"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <h3 className="text-lg font-bold text-[var(--dash-text)] truncate">
                          {agent.firstName} {agent.lastName}
                        </h3>
                        <Badge 
                          variant={agent.isActive ? "default" : "secondary"}
                          className={`text-xs ${agent.isActive ? "dash-pill-active" : "dash-pill-inactive"}`}
                        >
                          {agent.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge variant="outline" className="text-xs bg-[var(--dash-card)] text-[var(--dash-text)] border-[var(--dash-border)]">
                          {formatAgentType(agent)}
                        </Badge>
                        {agent.teamRole && (
                          <Badge variant="secondary" className="text-xs">
                            Team: {formatTeamRole(agent.teamRole)}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--dash-text-muted)]">
                        {agent.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-3.5 w-3.5 text-[var(--dash-text-muted)]" />
                            <span className="truncate">{formatPhone(agent.phone)}</span>
                          </div>
                        )}
                        {agent.email && (
                          <div className="flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5 text-[var(--dash-text-muted)]" />
                            <span className="truncate max-w-[200px]">{agent.email}</span>
                          </div>
                        )}
                        {agent.employeeId && (
                          <Badge variant="outline" className="text-xs bg-[var(--dash-card)] text-[var(--dash-text)] border-[var(--dash-border)]">
                            ID: {agent.employeeId}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Right: Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-[var(--dash-text-muted)] hover:text-[var(--dash-text)] hover:bg-[var(--dash-card)]"
                          title="Actions"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[var(--dash-card)] border border-[var(--dash-border)]" align="end">
                        {isLinkedToTeam(agent) ? (
                          <DropdownMenuItem asChild>
                            <Link href="/settings/team" className="flex items-center cursor-pointer text-[var(--dash-text)] hover:bg-[var(--dash-bg)]">
                              <User className="h-4 w-4 mr-2" />
                              <span>Manage in Team</span>
                            </Link>
                          </DropdownMenuItem>
                        ) : (
                          <>
                            <DropdownMenuItem 
                              onClick={() => handleEditAgent(agent)}
                              className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer"
                            >
                              <Edit2 className="h-4 w-4 mr-2" />
                              <span>Edit</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteAgent(agent)}
                              className="text-[var(--dash-pill-inactive)] hover:bg-[var(--dash-bg)] cursor-pointer"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              <span>Delete</span>
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                
                {/* Job Types & Pay Rate - Prominent Display (field-only agents; team-linked show manage in Team) */}
                <div className="mt-3 pt-3 border-t border-[var(--dash-border)] space-y-2">
                  {isLinkedToTeam(agent) ? (
                    <p className="text-xs text-[var(--dash-text-muted)]">
                      Team member — manage in <Link href="/settings/team" className="text-[var(--dash-accent)] hover:underline">Settings → Team</Link>
                    </p>
                  ) : (
                    <>
                      {/* Job Types */}
                      <div>
                        <div className="flex items-center gap-1.5 mb-1.5 text-xs text-[var(--dash-text-muted)] font-medium">
                          <Briefcase className="h-3.5 w-3.5" />
                          Job Types:
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(() => {
                            // Safely parse specialties for grid view
                            let specialtiesList: string[] = [];
                            if (agent.specialties) {
                              if (Array.isArray(agent.specialties)) {
                                specialtiesList = agent.specialties.filter(s => typeof s === 'string');
                              } else if (typeof agent.specialties === 'string') {
                                try {
                                  const parsed = JSON.parse(agent.specialties);
                                  specialtiesList = Array.isArray(parsed) ? parsed.filter(s => typeof s === 'string') : [];
                                } catch {
                                  specialtiesList = [];
                                }
                              }
                            }
                            
                            return specialtiesList.length > 0 ? (
                              specialtiesList.map((specialty: string, idx: number) => (
                                <Badge 
                                  key={idx}
                                  variant="outline" 
                                  className="text-xs bg-[var(--dash-card)] text-[var(--dash-text)] border-[var(--dash-border)]"
                                >
                                  {specialty}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-[var(--dash-text-muted)] italic">No specialties assigned</span>
                            );
                          })()}
                        </div>
                      </div>
                      
                      {/* Pay Rate - Prominent */}
                      <div className="flex items-center justify-between pt-2 border-t border-[var(--dash-border)]">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-[var(--dash-accent)]" />
                          <div>
                            <div className="text-xs text-[var(--dash-text-muted)] font-medium">Hourly Rate</div>
                            <div className="text-sm font-bold text-[var(--dash-accent)]">
                              {agent.hourlyRate ? `$${Number(agent.hourlyRate).toFixed(2)}/hr` : 'Not set'}
                            </div>
                          </div>
                        </div>
                        {agent.commissionRate && (
                          <div className="text-right">
                            <div className="text-xs text-[var(--dash-text-muted)] font-medium">Commission</div>
                            <div className="text-sm font-semibold text-[var(--dash-text)]">
                              {Number(agent.commissionRate).toFixed(1)}%
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Additional Details Row (field-only agents; team-linked skip) */}
                {!isLinkedToTeam(agent) && (
                <div className="mt-2 pt-2 border-t border-[var(--dash-border)]">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[var(--dash-text-muted)] font-medium">Hired:</span>
                      <span className="text-[var(--dash-text)]">{safeFormatDate(agent.hireDate)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[var(--dash-text-muted)] font-medium">Color:</span>
                      <div className="flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded-full border border-[var(--dash-border)]" style={{ backgroundColor: agent.colorCode || '#3B82F6' }}></span>
                        <span className="text-[var(--dash-text)]">{colorOptions.find(c => c.value === agent.colorCode)?.name || 'Custom'}</span>
                      </div>
                    </div>
                    {(() => {
                      // Safely parse specialties for display
                      let specialtiesList: string[] = [];
                      if (agent.specialties) {
                        if (Array.isArray(agent.specialties)) {
                          specialtiesList = agent.specialties.filter(s => typeof s === 'string');
                        } else if (typeof agent.specialties === 'string') {
                          try {
                            const parsed = JSON.parse(agent.specialties);
                            specialtiesList = Array.isArray(parsed) ? parsed.filter(s => typeof s === 'string') : [];
                          } catch {
                            specialtiesList = [];
                          }
                        }
                      }
                      
                      return specialtiesList.length > 0 && (
                        <div className="flex items-center gap-1.5 col-span-2">
                          <span className="text-[var(--dash-text-muted)] font-medium">Specialties:</span>
                          <span className="text-[var(--dash-text)] truncate">{specialtiesList.slice(0, 2).join(', ')}{specialtiesList.length > 2 ? ` +${specialtiesList.length - 2}` : ''}</span>
                        </div>
                      );
                    })()}
                  </div>
                  
                  {agent.notes && (
                    <div className="mt-2 pt-2 border-t border-[var(--dash-border)]">
                      <div className="text-xs text-[var(--dash-text-muted)] line-clamp-2">{agent.notes}</div>
                    </div>
                  )}
                </div>
                )}
              </div>
            ))}
            </div>
          )}

            {isError && (
              <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] p-12 text-center">
                <h3 className="text-xl font-bold text-[var(--dash-text)] mb-2">Could not load agents</h3>
                <p className="text-[var(--dash-text-muted)] mb-6 max-w-md mx-auto">
                  {error?.message || 'Something went wrong. Please try again.'}
                </p>
                <Button className="h-10 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white" onClick={() => refetchAgents()}>
                  Try again
                </Button>
              </div>
            )}
            {!isError && agents.length === 0 && (
              <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] p-12 text-center">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full bg-[var(--dash-card)] border-2 border-[var(--dash-border)] flex items-center justify-center">
                    <User className="h-10 w-10 text-[var(--dash-accent)]" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-[var(--dash-text)] mb-3">No Agents Found</h3>
                <p className="text-[var(--dash-text-muted)] mb-6 max-w-md mx-auto">
                  Get started by adding your first field agent to handle estimates and appointments.
                </p>
                <Button className="h-10 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white" onClick={() => openNewAgentDialog()}>
                  <Plus className="h-5 w-5 mr-2" />
                  Add Your First Agent
                </Button>
              </div>
            )}
          </div>

          {/* WORKERS SECTION */}
          <div className="mt-12 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[var(--dash-text)]">Workers</h2>
              <Button onClick={openNewWorkerDialog} className="h-10 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white">
                <Plus className="h-4 w-4 mr-2" />
                Add Worker
              </Button>
            </div>

            {/* Workers List View */}
            {viewMode === 'list' ? (
              <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-[var(--dash-border)] bg-[var(--dash-card)]">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">Worker</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">Work Types</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">Hourly Rate</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-[var(--dash-text-muted)] uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--dash-border)]">
                      {workers.map((worker: any) => (
                        <tr key={worker.id} className="hover:bg-[var(--dash-card)] transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[var(--dash-card)] border-2 border-[var(--dash-border)]">
                                <User className="h-5 w-5 text-[var(--dash-accent)]" />
                              </div>
                              <div>
                                <div className="font-semibold text-[var(--dash-text)]">
                                  {worker.firstName} {worker.lastName}
                                </div>
                                {worker.employeeId && (
                                  <div className="text-xs text-[var(--dash-text-muted)]">
                                    ID: {worker.employeeId}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1.5">
                              {worker.workTypes && worker.workTypes.length > 0 ? (
                                worker.workTypes.map((workType: string, idx: number) => (
                                  <Badge 
                                    key={idx}
                                    variant="outline" 
                                    className="text-xs bg-[var(--dash-card)] text-[var(--dash-text)] border-[var(--dash-border)]"
                                  >
                                    <Briefcase className="h-3 w-3 mr-1 inline" />
                                    {workType}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-xs text-[var(--dash-text-muted)] italic">No work types</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {worker.hourlyRate ? (
                              <div className="flex items-center gap-1.5 text-[var(--dash-accent)] font-semibold">
                                <DollarSign className="h-4 w-4" />
                                <span>${worker.hourlyRate.toFixed(2)}/hr</span>
                              </div>
                            ) : (
                              <span className="text-xs text-[var(--dash-text-muted)]">Not set</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge 
                              variant={worker.isActive ? "default" : "secondary"}
                              className={`text-xs ${worker.isActive ? "dash-pill-active" : "dash-pill-inactive"}`}
                            >
                              {worker.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[var(--dash-text-muted)] hover:text-[var(--dash-text)] hover:bg-[var(--dash-card)]">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-[var(--dash-card)] border border-[var(--dash-border)]" align="end">
                                <DropdownMenuItem onClick={() => handleEditWorker(worker)} className="text-[var(--dash-text)] hover:bg-[var(--dash-bg)] cursor-pointer">
                                  <Edit2 className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteWorker(worker)} className="text-[var(--dash-pill-inactive)] hover:bg-[var(--dash-bg)] cursor-pointer">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {workers.length === 0 && (
                    <div className="text-center py-12">
                      <User className="h-12 w-12 mx-auto mb-3 text-[var(--dash-text-muted)]" />
                      <p className="text-[var(--dash-text-muted)]">No workers found. Add your first worker to get started.</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Workers Grid View */
              <div className="grid gap-4">
                {workers.map((worker: any) => (
                  <Card key={worker.id} className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)]">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[var(--dash-card)] border-2 border-[var(--dash-border)]">
                            <User className="h-6 w-6 text-[var(--dash-accent)]" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{worker.firstName} {worker.lastName}</CardTitle>
                            {worker.employeeId && (
                              <p className="text-sm text-[var(--dash-text-muted)]">ID: {worker.employeeId}</p>
                            )}
                          </div>
                        </div>
                        <Badge variant="default" className={`text-xs ${worker.isActive ? "dash-pill-active" : "dash-pill-inactive"}`}>
                          {worker.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label className="text-xs text-[var(--dash-text-muted)] mb-2 block">Work Types</Label>
                        <div className="flex flex-wrap gap-1.5">
                          {worker.workTypes && worker.workTypes.length > 0 ? (
                            worker.workTypes.map((workType: string, idx: number) => (
                              <Badge key={idx} variant="outline" className="text-xs bg-[var(--dash-card)] text-[var(--dash-text)] border-[var(--dash-border)]">
                                {workType}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-xs text-[var(--dash-text-muted)] italic">No work types assigned</span>
                          )}
                        </div>
                      </div>
                      {worker.hourlyRate && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-[var(--dash-accent)]" />
                          <span className="font-semibold text-[var(--dash-accent)]">
                            ${worker.hourlyRate.toFixed(2)}/hr
                          </span>
                        </div>
                      )}
                      <div className="flex gap-2 pt-2 border-t border-[var(--dash-border)]">
                        <Button variant="outline" size="sm" onClick={() => handleEditWorker(worker)} className="flex-1">
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteWorker(worker)} className="text-[var(--dash-pill-inactive)] border-[var(--dash-border)]">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="schedule" className="space-y-6">
          <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-[var(--dash-text)] mb-2">Daily Schedule</h3>
                <p className="text-[var(--dash-text-muted)]">View and manage agent schedules for specific dates</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 mb-6">
              <Label htmlFor="scheduleDate" className="text-[var(--dash-text)] font-medium">Select Date:</Label>
              <Input
                id="scheduleDate"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-auto rounded-lg border-[var(--dash-border)] bg-[var(--dash-card)]"
              />
            </div>
            
            <AgentScheduler selectedDate={selectedDate} />
          </div>
        </TabsContent>
      </Tabs>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}